import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment-timezone'
import { get, patch } from 'helpers/api_helper'
import ListenerComponent from 'components/Common/Socket/ListenerComponent'
import { store as socketStore } from 'components/Common/Socket/store'
import useConfirmedOperation from 'components/Common/useConfirmedOperation'
import EventDateTime from '../../../EventDateTime'

// RELEASE-TENIS10 § 11 P92 (Tudor 2026-10-26, round 2; canvas R2 · 4, CP2-editor): a Tenis10 competition's lines in the tournament
// programme, one PDF for the whole event. The check-in is the start less 15 minutes and follows it until it is fixed by hand; „Reset to
// automatic” makes it follow again. „Important information” is one text of the event, so a change here changes it for every competition
// (and in SportyaOS). The lines save apart from the competition's details, and the programme shows them once it is published.

const LIMITS = { awards: 200, importantInfo: 500, note: 300 }
const timeout = { timeout: 15000 }
const REFUSALS = {
  FORBIDDEN: 'Your account cannot change this competition’s programme.',
  ERROR_PERMISSIONS: 'You cannot change this competition’s programme.',
  INVALID_PROGRAM: 'The programme lines could not be saved: review them and try again.',
  INVALID_PROGRAM_CHECK_IN: 'The check-in must be before the competition’s start.',
  INVALID_PROGRAM_TEXT: 'A programme text is longer than it may be.',
  INVALID_PROGRAM_EVENT: 'Only a Tenis10 competition has programme lines.',
  COMPETITION_NOT_FOUND: 'The competition no longer exists.',
  PROGRAM_UNCONFIRMED: 'The save could not be confirmed. Check status before saving again.',
}
const AUTO_MINUTES = 15
const linesOf = data => ({
  checkInAt: data?.competition?.program?.checkInAt ?? null,
  checkInAuto: data?.competition?.program?.checkInAuto !== false,
  awards: data?.competition?.program?.awards ?? '',
  note: data?.competition?.program?.note ?? '',
  importantInfo: data?.eventContext?.program?.importantInfo ?? '',
})
const same = (a, b) => a.checkInAuto === b.checkInAuto && (a.checkInAuto || new Date(a.checkInAt).getTime() === new Date(b.checkInAt).getTime())
  && ['awards', 'note', 'importantInfo'].every(field => a[field].trim() === b[field].trim())
// The lines a save sends: those changed since the read, the check-in as a date or null (automatic).
const changes = (saved, draft) => ({
  ...(draft.checkInAuto !== saved.checkInAuto || (!draft.checkInAuto && new Date(draft.checkInAt).getTime() !== new Date(saved.checkInAt).getTime())
    ? { checkInAt: draft.checkInAuto ? null : draft.checkInAt } : {}),
  ...Object.fromEntries(['awards', 'note', 'importantInfo'].filter(field => draft[field].trim() !== saved[field].trim()).map(field => [field, draft[field].trim()])),
})
// Enter in a line of the card never submits the competition's form around it.
const noSubmit = event => { if (event.key === 'Enter' && event.target.tagName !== 'TEXTAREA') event.preventDefault() }

export default function CompetitionProgramCard({ eventId, competitionId, timezone }) {
  const { state: { socket: { socketClientId } = {} } = {} } = useContext(socketStore)
  const operation = useConfirmedOperation()
  const [read, setRead] = useState(null)
  const [draft, setDraft] = useState(null)
  const [saved, setSaved] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const path = `/admin/events/${eventId}/competitions/${competitionId}`
  useEffect(() => {
    let active = true
    get(path).then(response => {
      if (!response?.data?.competition) throw new Error('Unavailable')
      if (active) { setRead({ data: response.data }); setDraft(linesOf(response.data)) }
    }).catch(() => { if (active) setRead({ error: true }) })
    return () => { active = false }
  }, [path, attempt])

  const section = children => <section className="card competition-program-card" aria-labelledby="competition-program-title"><div className="card-body" onKeyDown={noSubmit}>
    <h5 id="competition-program-title">Programme (Tenis10)</h5>{children}</div></section>
  if (!read || (!read.error && !draft)) return section(<p role="status">Loading the programme lines…</p>)
  if (read.error) return section(<div role="alert">Could not load the programme lines. <button type="button" className="btn btn-link" onClick={() => setAttempt(value => value + 1)}>Retry</button></div>)

  const stored = linesOf(read.data)
  const start = read.data.competition.startDate
  const automatic = start ? moment(start).subtract(AUTO_MINUTES, 'minutes').toISOString() : null
  const dirty = !same(stored, draft)
  const set = (field, value) => { setSaved(false); setDraft(current => ({ ...current, [field]: value })) }
  const save = () => {
    if (operation.busy || !socketClientId || !dirty) return
    const program = changes(stored, draft)
    const sent = { ...draft }
    operation.run({
      execute: () => patch(`${path}/program`, { program, socketClientId }, timeout),
      // Events answers with the competition it changed: never another competition's save.
      matchesSocket: payload => payload?.operation === 'competition-program' && payload?.competitionId === competitionId,
      refusals: REFUSALS,
      read: () => get(path),
      isComplete: response => Boolean(response?.data?.competition) && same(linesOf(response.data), sent),
      onSuccess: () => { setSaved(true); setAttempt(value => value + 1) },
    })
  }
  const counter = (field, help) => <small className="form-text text-muted">{help} · {draft[field].length} / {LIMITS[field]}</small>
  return section(<>
    <p className="text-muted">These lines appear in the tournament programme (one PDF for the whole event), in this competition's block. The programme stays
      hidden until it is published from the event's Competitions tab.</p>
    <div className="form-group">
      <span className="d-inline-flex align-items-center">
        <EventDateTime id="competition-program-checkin" label="Check-in" required timezone={timezone}
          value={draft.checkInAuto ? automatic : draft.checkInAt}
          onChange={value => { setSaved(false); setDraft(current => ({ ...current, checkInAuto: false, checkInAt: value })) }} />
      </span>
      <span className={'badge ml-2 ' + (draft.checkInAuto ? 'badge-light' : 'badge-warning')}>{draft.checkInAuto ? 'Automatic' : 'Manual'}</span>
      {!draft.checkInAuto && <button type="button" className="btn btn-outline-secondary btn-sm ml-2"
        onClick={() => { setSaved(false); setDraft(current => ({ ...current, checkInAuto: true, checkInAt: null })) }}>Reset to automatic</button>}
      <small className="form-text text-muted">{draft.checkInAuto
        ? '15 minutes before the start, by default; it moves with the start. Pick another date and hour to fix it yourself.'
        : 'Fixed: it no longer moves with the start. Must be before the start.'}</small>
    </div>
    <div className="form-group">
      <label htmlFor="competition-program-awards">Awards ceremony</label>
      <input id="competition-program-awards" className="form-control" maxLength={LIMITS.awards} value={draft.awards} onChange={event => set('awards', event.target.value)} />
      {counter('awards', 'Free text, shown as written (up to 200 characters)')}
    </div>
    <div className="form-group">
      <label htmlFor="competition-program-important">Important information — whole tournament</label> <span className="badge badge-info">Shared by every competition</span>
      <textarea id="competition-program-important" className="form-control" rows={3} maxLength={LIMITS.importantInfo} value={draft.importantInfo} onChange={event => set('importantInfo', event.target.value)} />
      {counter('importantInfo', 'One text for the event: editing it here changes it on every competition (CP and SportyaOS). Shown at the top of the programme')}
    </div>
    <div className="form-group">
      <label htmlFor="competition-program-note">Notes for this competition</label>
      <textarea id="competition-program-note" className="form-control" rows={2} maxLength={LIMITS.note} value={draft.note} onChange={event => set('note', event.target.value)} />
      {counter('note', 'Only this competition: shown under its list in the programme')}
    </div>
    <div hidden><ListenerComponent topic="events-response" listenFunction={operation.receive} /></div>
    {operation.phase === 'pending' && <p role="status">Saving the programme…</p>}
    {saved && !dirty && <p role="status">Programme saved.</p>}
    {operation.message && <p role="alert">{operation.message}{operation.phase === 'uncertain' && <button type="button" className="btn btn-link" onClick={operation.verify}>Check status</button>}</p>}
    <button type="button" className="btn btn-primary" disabled={!dirty || operation.busy || !socketClientId} onClick={save}>{operation.phase === 'pending' ? 'Saving programme…' : 'Save programme'}</button>
  </>)
}
